/**
 * SupporterBadge Component
 *
 * A subtle badge shown in the sidebar that indicates supporter status
 * and provides a way to become a supporter or manage subscription.
 *
 * States:
 * - Not authenticated: Hidden
 * - Free user: Shows "Support Us" with heart icon
 * - Trialing: Shows "Trial: X days left" badge
 * - Supporter: Shows "Supporter" badge with checkmark
 * - Canceled: Shows "Subscription ending" with date
 */

import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSupporter } from '../contexts/SupporterContext';
import SupporterModal from './SupporterModal';
import './SupporterBadge.css';

interface SupporterBadgeProps {
  /** Compact mode for smaller displays */
  compact?: boolean;
}

export default function SupporterBadge({ compact = false }: SupporterBadgeProps) {
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const {
    status,
    trialEndsAt,
    currentPeriodEnd,
    cancelAtPeriodEnd,
    isLoading,
    isSupporter,
  } = useSupporter();
  const [showModal, setShowModal] = useState(false);

  // Don't show if not authenticated or still loading
  if (!isAuthenticated || authLoading) {
    return null;
  }

  // Calculate days remaining for trial
  const getDaysRemaining = (dateStr: string | null): number => {
    if (!dateStr) return 0;
    const end = new Date(dateStr);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  const trialDays = getDaysRemaining(trialEndsAt);
  const periodEndDays = getDaysRemaining(currentPeriodEnd);

  // Determine what to show
  let badgeContent: React.ReactNode;
  let badgeClass = 'supporter-badge';
  let tooltipText = '';

  if (isLoading) {
    badgeContent = (
      <>
        <span className="supporter-badge-icon">...</span>
        {!compact && <span className="supporter-badge-text">Loading</span>}
      </>
    );
  } else if (status === 'trialing') {
    badgeClass += ' supporter-badge-trial';
    tooltipText = `Trial ends in ${trialDays} day${trialDays !== 1 ? 's' : ''}`;
    badgeContent = (
      <>
        <span className="supporter-badge-icon">&#x23F3;</span>
        {!compact && (
          <span className="supporter-badge-text">
            Trial: {trialDays}d left
          </span>
        )}
      </>
    );
  } else if (isSupporter) {
    if (cancelAtPeriodEnd) {
      badgeClass += ' supporter-badge-ending';
      tooltipText = `Subscription ends in ${periodEndDays} day${periodEndDays !== 1 ? 's' : ''}`;
      badgeContent = (
        <>
          <span className="supporter-badge-icon">&#x26A0;</span>
          {!compact && (
            <span className="supporter-badge-text">Ending soon</span>
          )}
        </>
      );
    } else {
      badgeClass += ' supporter-badge-active';
      tooltipText = 'Thank you for supporting Hipponics Firewatch!';
      badgeContent = (
        <>
          <span className="supporter-badge-icon">&#x2764;</span>
          {!compact && <span className="supporter-badge-text">Supporter</span>}
        </>
      );
    }
  } else {
    // Free tier or expired - show "Support Us"
    badgeClass += ' supporter-badge-cta';
    tooltipText = 'Help keep Hipponics Firewatch running';
    badgeContent = (
      <>
        <span className="supporter-badge-icon">&#x2661;</span>
        {!compact && <span className="supporter-badge-text">Support Us</span>}
      </>
    );
  }

  return (
    <>
      <button
        className={badgeClass}
        onClick={() => setShowModal(true)}
        title={tooltipText}
        aria-label={tooltipText}
      >
        {badgeContent}
      </button>

      {showModal && (
        <SupporterModal onClose={() => setShowModal(false)} />
      )}
    </>
  );
}
